import { X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ModalShell } from '@/components/ModalShell';
import { useT, useLang } from '@/i18n';
import { kindOf } from '@/features/audit/f5-kinds';
import { entityTypeToPath } from '@/features/audit/entity-link';
import { humanizeAction, humanizeEntity } from '@/features/audit/humanize';
import { cn } from '@/lib/utils';
import type { AuditLogEntry } from '@/types/api';

// ---------------------------------------------------------------------------
// F5-T6b · Admin 09 — el modal «Ver detalle» al contrato: título
// «{verbo} · {entidad}», stamp con el id, los tres bloques (Quién · Sobre
// qué · Desde dónde), la caja «Qué cambió» campo por campo con cápsulas
// antes→ahora, la caja de contexto para lecturas, y el pie «inmutable» con
// «Abrir {entidad}».
//
// MUERE con el contrato (dies #3): el volcado de JSON crudo del payload —
// reemplazado por el diff campo por campo (metadata.diff {before,after}) o
// por filas campo/valor legibles.
//
// NECESITA-SERVER:
//   · Rol del actor AL MOMENTO del evento (snapshot) — el entry no lo trae;
//     se muestra el email, no un rol resuelto contra hoy (eso mentiría).
//   · Sesión/dispositivo bajo «Desde dónde» — solo hay ipAddress.
//   · Meta-auditoría de abrir este detalle (el detalle es client-side).
// ---------------------------------------------------------------------------

interface AuditDetailModalProps {
  entry: AuditLogEntry | null;
  onClose: () => void;
}

const BLOCK_LABEL =
  'font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate m-0 mb-1.5';

export function AuditDetailModal({ entry, onClose }: AuditDetailModalProps): JSX.Element | null {
  const t = useT();
  const lang = useLang();
  if (!entry) return null;

  const kind = kindOf(entry.action);
  const dt = new Date(entry.occurredAt);
  const humanAction = humanizeAction(entry.action, lang);
  const humanEntity = humanizeEntity(entry.entityType, lang);
  const entityPath = entityTypeToPath(entry.entityType, entry.entityId);
  const md = entry.metadata && typeof entry.metadata === 'object'
    ? (entry.metadata as Record<string, unknown>)
    : null;
  const diff = extractDiff(md);
  const contextEntries = md
    ? Object.entries(md).filter(([k]) => k !== 'diff').slice(0, 10)
    : [];

  return (
    <ModalShell
      open={!!entry}
      onClose={onClose}
      ariaLabelledBy="audit-detail-title"
      widthClass="max-w-[620px]"
    >
      <header className="bg-white border-b border-nav-hair px-7 py-[26px] pb-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2
            id="audit-detail-title"
            className="font-head text-[19px] font-bold tracking-[-0.01em] text-nav-navy m-0 truncate"
          >
            {humanAction}
            {humanEntity ? ` · ${humanEntity}` : ''}
          </h2>
          <p className="text-[13px] text-nav-slate tabular-nums mt-1 m-0">
            {t('f5t6b.au.m.stamp', { time: dt.toLocaleString(), id: entry.id })}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('f5t6b.au.m.close')}
          className="h-11 w-11 min-h-[44px] shrink-0 inline-flex items-center justify-center rounded-lg text-nav-slate hover:bg-nav-softer hover:text-nav-navy transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          <X className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
      </header>

      <div className="modal-scroll flex-1 overflow-y-auto bg-white">
        <div className="px-7 py-5">
          {/* Quién · Sobre qué · Desde dónde */}
          <div className="grid gap-4 mb-5 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
            <div>
              <p className={BLOCK_LABEL}>{t('f5t6b.au.m.who')}</p>
              <p className="m-0 text-[14px] font-semibold text-nav-navy truncate">
                {entry.userName ?? t('f5t6b.au.system')}
              </p>
              {/* NECESITA-SERVER: rol AL MOMENTO del evento — no se guarda;
                  mostrar el rol de hoy mentiría. Se muestra el email. */}
              {entry.userEmail && (
                <p className="m-0 mt-0.5 text-[12.5px] text-nav-slate truncate">{entry.userEmail}</p>
              )}
            </div>
            <div>
              <p className={BLOCK_LABEL}>{t('f5t6b.au.m.what')}</p>
              {entry.entityType ? (
                <>
                  {entityPath && entry.entityId ? (
                    <Link
                      to={entityPath}
                      onClick={onClose}
                      className="text-[14px] font-semibold text-nav-teal underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal rounded"
                    >
                      {humanEntity} #{entry.entityId}
                    </Link>
                  ) : (
                    <p className="m-0 text-[14px] font-semibold text-nav-navy">
                      {humanEntity}
                      {entry.entityId ? (
                        <span className="text-nav-slate tabular-nums"> #{entry.entityId}</span>
                      ) : null}
                    </p>
                  )}
                  <p className="m-0 mt-0.5 text-[12.5px] text-nav-slate">{entry.entityType}</p>
                </>
              ) : (
                <p className="m-0 text-[14px] text-nav-slate">—</p>
              )}
            </div>
            <div>
              <p className={BLOCK_LABEL}>{t('f5t6b.au.m.where')}</p>
              <p className="m-0 text-[14px] font-semibold text-nav-navy tabular-nums">
                {entry.ipAddress ?? '—'}
              </p>
              {/* NECESITA-SERVER: sesión/dispositivo — solo hay IP. */}
            </div>
          </div>

          {/* Qué cambió (diff campo por campo) o contexto del acceso. */}
          {diff.length > 0 ? (
            <div className="bg-nav-softer border-[1.5px] border-nav-card rounded-xl px-[18px] py-4">
              <p className={BLOCK_LABEL}>{t('f5t6b.au.m.changed')}</p>
              <div className="flex flex-col gap-[11px]">
                {diff.map((d) => (
                  <div
                    key={d.field}
                    className="flex flex-wrap items-center gap-2"
                    aria-label={`${d.field}, ${t('f5t6b.au.m.before')} ${d.from}, ${t('f5t6b.au.m.after')} ${d.to}`}
                  >
                    <span className="flex-1 min-w-[130px] text-[13px] text-nav-navy">{d.field}</span>
                    <span className="inline-flex items-center min-h-[30px] px-[11px] rounded-lg bg-nav-hair border border-nav-card font-head text-[12.5px] font-bold text-nav-faint line-through tabular-nums">
                      {d.from}
                    </span>
                    <ArrowRight className="h-[15px] w-[15px] text-nav-faint shrink-0" aria-hidden="true" />
                    <span className="inline-flex items-center min-h-[30px] px-[11px] rounded-lg bg-nav-tealTint border border-nav-teal/30 font-head text-[12.5px] font-bold text-nav-navy tabular-nums">
                      {d.to}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : contextEntries.length > 0 ? (
            <div
              className={cn(
                'rounded-xl px-[18px] py-4 border-[1.5px]',
                kind === 'lectura'
                  ? 'bg-nav-tealTint border-nav-teal/30 text-nav-tealDark'
                  : 'bg-nav-softer border-nav-card text-nav-navy',
              )}
            >
              <p
                className={cn(
                  'font-head text-[10.5px] font-bold tracking-[0.12em] uppercase m-0 mb-1.5',
                  kind === 'lectura' ? 'text-nav-tealDark' : 'text-nav-slate',
                )}
              >
                {kind === 'lectura' ? t('f5t6b.au.m.context') : t('f5t6b.au.m.changed')}
              </p>
              <dl className="m-0 grid gap-x-4 gap-y-1.5 [grid-template-columns:auto_1fr]">
                {contextEntries.map(([k, v]) => (
                  <FieldRow key={k} field={k} value={v} />
                ))}
              </dl>
            </div>
          ) : null}
        </div>
      </div>

      <footer className="bg-white border-t border-nav-hair px-7 py-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-[12.5px] text-nav-slate tabular-nums">
          {t('f5t6b.au.m.immutable', { id: entry.id })}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center min-h-[44px] px-[18px] rounded-lg bg-white border-[1.5px] border-nav-line font-head text-[13.5px] font-bold text-nav-navy hover:bg-nav-softer hover:border-nav-faint transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            {t('f5t6b.au.m.close')}
          </button>
          {entityPath && (
            <Link
              to={entityPath}
              onClick={onClose}
              className="inline-flex items-center min-h-[44px] px-[18px] rounded-lg bg-nav-navy border-[1.5px] border-nav-navy font-head text-[13.5px] font-bold text-white hover:bg-nav-navyHover transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
            >
              {t('f5t6b.au.m.open', { entity: humanEntity || entry.entityType || '' })}
            </Link>
          )}
        </div>
      </footer>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------

interface DiffRow {
  field: string;
  from: string;
  to: string;
}

/**
 * Extrae el diff campo por campo cuando la metadata lo trae con la forma
 * canónica `{ diff: { campo: { before, after } } }` (USER_UPDATE,
 * PROFILE_UPDATED, …). Sin esa forma no se inventa un antes/después.
 */
function extractDiff(md: Record<string, unknown> | null): DiffRow[] {
  if (!md || typeof md.diff !== 'object' || md.diff === null) return [];
  const rows: DiffRow[] = [];
  for (const [field, change] of Object.entries(md.diff as Record<string, unknown>)) {
    if (change && typeof change === 'object' && 'before' in change && 'after' in change) {
      const c = change as { before: unknown; after: unknown };
      rows.push({
        field: humanizeFieldName(field),
        from: shortVal(c.before),
        to: shortVal(c.after),
      });
    }
  }
  return rows;
}

function FieldRow({ field, value }: { field: string; value: unknown }): JSX.Element {
  return (
    <>
      <dt className="text-[13px] font-semibold">{humanizeFieldName(field)}</dt>
      <dd className="m-0 text-[13px] leading-relaxed break-words tabular-nums">{shortVal(value)}</dd>
    </>
  );
}

function shortVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length > 120 ? `${v.slice(0, 117)}…` : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.length <= 6 ? v.map(shortVal).join(', ') : `${v.length} ítems`;
  const s = JSON.stringify(v);
  return s.length > 120 ? `${s.slice(0, 117)}…` : s;
}

function humanizeFieldName(s: string): string {
  const spaced = s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .trim();
  if (!spaced) return s;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
